import React, { useCallback, useEffect, useState } from "react";
import { Bell, CheckCheck, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { api, apiError } from "../../lib/api";
import { formatDateTime, relativeTime } from "../../lib/format";
import { cn } from "../../lib/utils";

export default function Notifications() {
  const [state, setState] = useState({ items: [], unread: 0, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/client/notifications", { params: { page, limit: 20 } });
      setState(data);
    } catch (error) {
      toast.error(apiError(error, "Could not load notifications"));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, successMessage) => {
    setBusy(true);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      await load();
    } catch (error) {
      toast.error(apiError(error, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Market updates"
        subtitle={
          state.unread > 0
            ? `${state.unread} unread update${state.unread === 1 ? "" : "s"}`
            : "You are all caught up"
        }
        actions={
          state.items.length ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={busy || state.unread === 0}
                onClick={() =>
                  act(() => api.post("/api/client/notifications/read-all"), "All updates marked as read")
                }
                data-testid="notifications-mark-all-read-button"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCheck className="mr-2 h-4 w-4" />}
                Mark all read
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() =>
                  act(() => api.post("/api/client/notifications/clear-all"), "Notifications cleared")
                }
                data-testid="notifications-clear-button"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Clear all
              </Button>
            </div>
          ) : null
        }
      />

      {loading ? (
        <div className="space-y-3" data-testid="page-loading-skeleton">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : state.items.length === 0 ? (
        <EmptyState
          icon={Bell}
          title="No updates right now"
          description="Market notes and account updates from your relationship manager will appear here."
          testId="notifications-empty-state"
        />
      ) : (
        <ul className="space-y-3">
          {state.items.map((item) => (
            <li
              key={item.id}
              className={cn(
                "flex gap-3 rounded-xl border bg-card p-4 transition-colors",
                item.read ? "border-border" : "border-primary/40"
              )}
              data-testid={`notification-item-${item.id}`}
            >
              <span
                className={cn(
                  "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                  item.read ? "bg-muted-foreground/40" : "bg-primary"
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold">{item.title}</p>
                  {!item.read ? (
                    <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                      New
                    </span>
                  ) : null}
                </div>
                <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                  {item.message}
                </p>
                <p className="mt-2.5 text-xs text-muted-foreground">
                  {formatDateTime(item.createdAt)} · {relativeTime(item.createdAt)}
                  {item.createdByName ? ` · ${item.createdByName}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-1.5">
                {!item.read ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => act(() => api.post(`/api/client/notifications/${item.id}/read`))}
                    data-testid="notification-mark-read-button"
                  >
                    <CheckCheck className="h-4 w-4" />
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    act(() => api.post(`/api/client/notifications/${item.id}/clear`), "Update cleared")
                  }
                  data-testid="notification-delete-button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {state.pages > 1 ? (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            data-testid="notifications-prev"
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {state.pages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= state.pages}
            onClick={() => setPage((p) => p + 1)}
            data-testid="notifications-next"
          >
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
}
